// Import database API
import {
    getAllProducts,
    getProductById,
    getProductImages,
    createProduct as databaseCreateProduct,
    deleteProduct as databaseDeleteProduct,
    editProduct as databaseEditProduct,
    addImageToProduct as databaseAddImageToProduct,
    deleteImage as databaseDeleteImage,
} from '../database/endpoints/products.api';

import {
    getProductImageSignedUrl,
    addImageToBucket,
    deleteImageFromBucket,
} from '../database/endpoints/images.api';

// The default time after signed urls are invalid
const SIGNED_URL_TIMEOUT = Number(
    process.env.REACT_APP_DATABASE_SIGNED_URL_TIMEOUT,
);

// ------------------------------------------------------------------------------
// FETCHER
// ------------------------------------------------------------------------------

// Function to fetch image signed url and store it into url field of the image
export async function fetchImageUrl(image) {
    try {
        // Get the image signed url
        const imageSignedUrl = await getProductImageSignedUrl(
            image.path,
            SIGNED_URL_TIMEOUT,
        );

        // If the signed url generated is invalid
        if (!imageSignedUrl || !imageSignedUrl.length > 0) {
            throw new Error('Wrong URL');
        }

        // Store the signed url into the field of the image
        image.url = imageSignedUrl;
    } catch (err) {
        image.url = null;
        console.warn(
            `Error while generating signed URL image with id: ${image.id}\nError message: ${err.message}`,
        );
    }
}

// Function to fetch all product images and store them under a filed of the object as:
// images = [{
//          id:
//          path:
//          url:
//          position:
// }]
export async function fetchProductImages(product) {
    try {
        // Get product images
        const productImages = await getProductImages(product.id);

        // If no images were found
        if (!productImages || !productImages.length > 0) {
            throw new Error('No images were found for this product');
        }

        // Generate a table of images, with the data fetched from db
        product.images = productImages.map((image) => ({
            id: image.id,
            path: image.image_path,
            url: null,
            position: image.image_position,
        }));

        // Parallelize the generation of image signed URL
        await Promise.allSettled(product.images.map(fetchImageUrl));

        // Sort images using their position
        product.images.sort(
            (firstImage, secondImage) =>
                firstImage.position - secondImage.position,
        );
    } catch (err) {
        product.images = [];
        console.warn(
            `Error while loading images for product with id: ${product.id}\nError message: ${err.message}`,
        );
    }
}

// Function to fetch all product datas and return them under a JSON as:
// productList = [{
//      id:
//      name:
//      description:
//      price:
//      is_offer:
//      images: [{
//          id:
//          path:
//          url:
//          position:
//      }]
// }]
export async function fetchProducts() {
    try {
        // Get all products
        const products = await getAllProducts();

        // If no product were found
        if (!products || !products.length > 0) {
            return null;
        }

        // Generate a list including all product with a image list,
        // initialized to null
        const productsWithImage = products.map((product) => ({
            ...product,
            images: [],
        }));

        // Parallelize the fetching of product images
        await Promise.allSettled(productsWithImage.map(fetchProductImages));

        return productsWithImage;
    } catch (err) {
        console.error(
            `Error while loading product data\nError message: ${err.message}`,
        );
        throw err;
    }
}

// Function to fetch a product datas by its id and return them under a JSON as:
// product = {
//      id:
//      name:
//      description:
//      price:
//      is_offer:
//      images: [{
//          id:
//          path:
//          url:
//          position:
//      }]
// }
export async function fetchProduct(productId) {
    try {
        // Get the product
        const product = await getProductById(productId);

        // If no product were found
        if (!product || !product.length > 0) {
            return null;
        }

        // Initialise an empty list of images for the product
        const productWithImage = { ...product[0], images: [] };

        // Get the images of the product
        await fetchProductImages(productWithImage);

        return productWithImage;
    } catch (err) {
        console.error(
            `Error while loading product data\nError message: ${err.message}`,
        );
        throw err;
    }
}

// ------------------------------------------------------------------------------
// UPDATERS
// ------------------------------------------------------------------------------

// Function to create a new product
export async function createProduct(
    productName,
    productDescription,
    productIsOffer,
    productPrice,
    productImages,
) {
    // Create a list to store every image correctly uploaded into the bucket
    const validImagePaths = [];

    // If there is product images
    if (productImages && productImages.length > 0) {
        for (const imagetToAdd of productImages) {
            try {
                // Try to upload images to the bucket
                await addImageToBucket(imagetToAdd.path, imagetToAdd.file);
                validImagePaths.push(imagetToAdd.path);
            } catch (err) {
                console.error(
                    `Error while uploading image ${imagetToAdd.path}\nError message: ${err.message}`,
                );
            }
        }
    }

    try {
        // Try to create the product and add its images to the database
        const newProductId = await databaseCreateProduct(
            productName,
            productDescription,
            productIsOffer,
            productPrice,
            validImagePaths.length > 0 ? validImagePaths : null,
        );

        return newProductId;
    } catch (err) {
        console.error(
            `Error while creating the new product\nError message: ${err.message}`,
        );

        // Delete the correctly uploaded images if the product failed to be created
        if (validImagePaths && validImagePaths.length > 0) {
            for (const imagePathToDelete of validImagePaths) {
                try {
                    // Try to delete image from the bucket
                    await deleteImageFromBucket(imagePathToDelete);
                } catch (err) {
                    console.error(
                        `Error while deleting image ${imagePathToDelete} from the bucket\nError message: ${err.message}`,
                    );
                }
            }
        }

        throw err;
    }
}

// Function to delete a product and its images
export async function deleteProduct(productId, productImages) {
    try {
        // Try to delete the product
        await databaseDeleteProduct(productId);
    } catch (err) {
        console.error(
            `Error while deleting the product with id ${productId}\nError message: ${err.message}`,
        );

        throw err;
    }

    if (productImages && productImages.length > 0) {
        // Map into product images list to delete them
        for (const imageToDelete of productImages) {
            try {
                // Try to delete the image
                await deleteImageFromBucket(imageToDelete.path);
            } catch (err) {
                console.error(
                    `Error while deleting image ${imageToDelete.path} from the bucket\nError message: ${err.message}`,
                );
            }
        }
    }
}

// Function to edit a product fileds
export async function editProduct(
    productId,
    productNewName,
    productNewDescription,
    productNewIsOffer,
    productNewPrice,
) {
    try {
        // Try to edit product fileds
        await databaseEditProduct(
            productId,
            productNewName,
            productNewDescription,
            productNewIsOffer,
            productNewPrice,
        );
    } catch (err) {
        console.error(
            `Error while creating the product with id ${productId}\nError message: ${err.message}`,
        );

        throw err;
    }
}

// Function to add an image to a product
export async function addImageToProduct(productId, imagePath, imageFile) {
    let newImageId = null;

    try {
        // Try to add the image to the product
        newImageId = await databaseAddImageToProduct(productId, imagePath);
    } catch (err) {
        console.error(
            `Error while adding the image ${imagePath} to product with id ${productId}\nError message: ${err.message}`,
        );

        throw err;
    }

    try {
        // Try to upload the image
        await addImageToBucket(imagePath, imageFile);
    } catch (err) {
        console.error(
            `Error while uploading image ${imagePath}\nError message: ${err.message}`,
        );

        try {
            // Try to delete the image from the db
            await databaseDeleteImage(newImageId);
        } catch (err) {
            console.error(
                `Error while deleting the image ${imagePath}\nError message: ${err.message}`,
            );

            throw err;
        }

        throw err;
    }
}

// Function to remove image from database
export async function deleteImage(imageId, imagePath) {
    try {
        // Try to delete the image from the db
        await databaseDeleteImage(imageId);
    } catch (err) {
        console.error(
            `Error while deleting the image ${imagePath}\nError message: ${err.message}`,
        );

        throw err;
    }

    try {
        // Try to delete image from the bucket
        await deleteImageFromBucket(imagePath);
    } catch (err) {
        console.error(
            `Error while deleting image ${imagePath} from the bucket\nError message: ${err.message}`,
        );
    }
}
